import { ActionIcon, Badge, Group, Table, Text, Tooltip } from "@mantine/core";
import { IconFileDownload } from "@tabler/icons-react";
import type { PlatformInvoice } from "../../../../apis/platformInvoice";
import { InvoiceStatusBadge } from "../../../../components/billing/InvoiceStatusBadge";
import { MoneyCell } from "../../../../components/billing/MoneyCell";
import { formatDate } from "../../../../utils/format";

const invoiceTypeLabel: Record<string, string> = {
  SUBSCRIPTION_ONLY: "Subscription",
  USAGE_ONLY       : "Usage",
  COMBINED         : "Нэгдсэн"
};

const invoiceTypeColor: Record<string, string> = {
  SUBSCRIPTION_ONLY: "violet",
  USAGE_ONLY       : "cyan",
  COMBINED         : "indigo"
};

interface Props {
  rows: PlatformInvoice[];
  onRowClick?: (invoice: PlatformInvoice) => void;
  /** Open the invoice PDF in a new tab. When omitted the action is disabled. */
  onOpenPdf?: (invoice: PlatformInvoice) => void;
}

const period = (start: string, end: string) => {
  const s = start?.substring(0, 7) ?? "";
  const e = end?.substring(0, 7) ?? "";
  return s === e || !e ? s : `${s} – ${e}`;
};

export function InvoiceTable({ rows, onRowClick, onOpenPdf }: Props) {
  const pdfAvailable = Boolean(onOpenPdf);

  return (
    <Table.ScrollContainer minWidth={760}>
      <Table verticalSpacing="sm" highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Нэхэмжлэх №</Table.Th>
            <Table.Th>Төрөл</Table.Th>
            <Table.Th>Хугацаа</Table.Th>
            <Table.Th>Гарсан</Table.Th>
            <Table.Th>Төлөх хугацаа</Table.Th>
            <Table.Th ta="right">Дүн</Table.Th>
            <Table.Th>Төлөв</Table.Th>
            <Table.Th ta="right">Үйлдэл</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((r) => (
            <Table.Tr
              key={r.id}
              style={onRowClick ? { cursor: "pointer" } : undefined}
              onClick={onRowClick ? () => onRowClick(r) : undefined}
            >
              <Table.Td>
                <Text style={{ fontVariantNumeric: "tabular-nums" }}>{r.invoiceNumber}</Text>
              </Table.Td>
              <Table.Td>
                <Badge variant="light" color={invoiceTypeColor[r.invoiceType] ?? "gray"} radius="sm">
                  {invoiceTypeLabel[r.invoiceType] ?? r.invoiceType}
                </Badge>
              </Table.Td>
              <Table.Td>
                <Text size="sm" style={{ fontVariantNumeric: "tabular-nums" }}>
                  {period(r.periodStart, r.periodEnd)}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text size="sm" c="dimmed" style={{ fontVariantNumeric: "tabular-nums" }}>
                  {formatDate(r.issueDate)}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text size="sm" c="dimmed" style={{ fontVariantNumeric: "tabular-nums" }}>
                  {formatDate(r.dueDate)}
                </Text>
              </Table.Td>
              <Table.Td ta="right">
                <MoneyCell value={r.totalAmount} currency={r.currency} fw={600} />
              </Table.Td>
              <Table.Td>
                <InvoiceStatusBadge status={r.status} />
              </Table.Td>
              <Table.Td ta="right">
                <Group justify="flex-end" gap="xs" wrap="nowrap">
                  <Tooltip label={pdfAvailable ? "PDF харах" : "PDF татах боломж удахгүй"} withArrow>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      disabled={!pdfAvailable}
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenPdf?.(r);
                      }}
                      aria-label="PDF харах"
                    >
                      <IconFileDownload size={18} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}
